import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Modal } from "components";

export type SignatureModalMode = "add" | "view" | "edit";

interface SignModalProps {
    open: boolean;
    setOpen: (value: boolean) => void;
    mode: SignatureModalMode;
    signature?: string | null;
    onSave?: (dataUrl: string) => void;
    loading?: boolean;
}

const modeConfig = {
    add: {
        title: "Add Signature",
        description:
            "Add your signature to your profile. You can either draw your signature or upload an image of it.",
    },
    edit: {
        title: "Edit Signature",
        description:
            "Update your signature. You can either draw a new signature or upload an image of it.",
    },
    view: {
        title: "Your Signature",
        description: "Your saved signature.",
    },
};

const SignatureModal = ({
    open,
    setOpen,
    mode,
    signature,
    onSave,
    loading,
}: SignModalProps) => {
    const [tab, setTab] = useState<"draw" | "upload">("draw");
    const [uploadPreview, setUploadPreview] = useState<string | null>(null);
    const [uploadError, setUploadError] = useState("");
    const sigRef = useRef<SignatureCanvas>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const isViewMode = mode === "view";
    const { title, description } = modeConfig[mode];

    useEffect(() => {
        if (!open) {
            setTab("draw");
            setUploadPreview(null);
            setUploadError("");
            sigRef.current?.clear();
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    }, [open]);

    const handleClear = () => {
        sigRef.current?.clear();
    };

    const handleDrawSave = () => {
        if (!sigRef.current || sigRef.current.isEmpty()) return;
        const dataUrl = sigRef.current.toDataURL("image/png");
        onSave?.(dataUrl);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUploadError("");
        const file = e.target.files?.[0];
        if (!file) return;
        const allowed = [
            "image/svg+xml",
            "image/png",
            "image/jpeg",
            "image/jpg",
        ];
        if (!allowed.includes(file.type)) {
            setUploadError("Only SVG, PNG, or JPG files are allowed.");
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            setUploadPreview(ev.target?.result as string);
        };
        reader.readAsDataURL(file);
    };

    const handleUploadSave = () => {
        if (!uploadPreview) return;
        onSave?.(uploadPreview);
    };

    return (
        <Modal
            open={open}
            setOpen={setOpen}
            title={title}
            description={description}
            size="xl"
            loading={loading}
            hideActions={isViewMode}
            renderActions={() => (
                <div className="flex justify-end gap-4 ml-5">
                    {tab === "upload" && uploadPreview && (
                        <div className="flex justify-between gap-4">
                            <button
                                type="button"
                                disabled={loading}
                                onClick={() => {
                                    setUploadPreview(null);
                                    if (fileInputRef.current)
                                        fileInputRef.current.value = "";
                                }}
                                className="text-sm text-secondary-500 hover:text-secondary-700 underline disabled:opacity-50">
                                Remove
                            </button>
                            <button
                                type="button"
                                disabled={loading}
                                onClick={handleUploadSave}
                                className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
                                {loading ? "Saving..." : "Save Signature"}
                            </button>
                        </div>
                    )}
                    {tab === "draw" && (
                        <div className="flex justify-between gap-4">
                            <button
                                type="button"
                                disabled={loading}
                                onClick={handleClear}
                                className="text-sm text-secondary-500 hover:text-secondary-700 underline disabled:opacity-50">
                                Clear
                            </button>
                            <button
                                type="button"
                                disabled={loading}
                                onClick={handleDrawSave}
                                className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
                                {loading ? "Saving..." : "Save Signature"}
                            </button>
                        </div>
                    )}
                </div>
            )}>
            <div className="w-full">
                {!isViewMode && (
                    <div className="flex border-b border-secondary-200 px-6">
                        {(["draw", "upload"] as const).map((t) => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => setTab(t)}
                                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors capitalize ${
                                    tab === t
                                        ? "border-blue-600 text-blue-700"
                                        : "border-transparent text-secondary-500 hover:text-secondary-700"
                                }`}>
                                {t === "draw" ? "Draw Signature" : "Upload File"}
                            </button>
                        ))}
                    </div>
                )}
                <div className="p-6">
                    {isViewMode ? (
                        <div className="border-2 border-dashed border-secondary-300 rounded-xl p-8 flex items-center justify-center bg-secondary-50 min-h-[200px]">
                            {signature ? (
                                <img
                                    src={signature}
                                    alt="Your signature"
                                    className="max-h-48 object-contain"
                                />
                            ) : (
                                <p className="text-sm text-secondary-500">
                                    No signature on file.
                                </p>
                            )}
                        </div>
                    ) : tab === "draw" ? (
                        <div className="space-y-4">
                            <p className="text-sm text-secondary-500">
                                Sign in the box below using your mouse or
                                finger.
                            </p>
                            <div className="border-2 border-dashed border-secondary-300 rounded-xl overflow-hidden bg-secondary-50">
                                <SignatureCanvas
                                    ref={sigRef}
                                    penColor="#1e293b"
                                    canvasProps={{
                                        width: 460,
                                        height: 200,
                                        className: "w-full",
                                        style: { touchAction: "none" },
                                    }}
                                    backgroundColor="transparent"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-sm text-secondary-500">
                                Upload an SVG, PNG, or JPG of the signature.
                            </p>
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                className="border-2 border-dashed border-secondary-300 rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                                {uploadPreview ? (
                                    <img
                                        src={uploadPreview}
                                        alt="Signature preview"
                                        className="max-h-32 object-contain"
                                    />
                                ) : (
                                    <>
                                        <svg
                                            className="w-10 h-10 text-secondary-300 mb-3"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={1.5}
                                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                            />
                                        </svg>
                                        <p className="text-sm text-secondary-500">
                                            Click to browse or drag & drop
                                        </p>
                                        <p className="text-xs text-secondary-400 mt-1">
                                            SVG, PNG, JPG supported
                                        </p>
                                    </>
                                )}
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg"
                                className="hidden"
                                onChange={handleFileChange}
                            />
                            {uploadError && (
                                <p className="text-sm text-red-600">
                                    {uploadError}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default SignatureModal;
